import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Sparkles, Rocket, Users, TrendingUp, Award, Target, ArrowUp, Briefcase, ChevronDown } from 'lucide-react';
import JobCard from '../components/jobs/JobCard';
import jobService, { Job, JobFilters } from '../services/jobService';
import { BenefitCard } from '../components/animations/BenefitCard';
import { ANIMATION_VARIANTS, HERO_CONTENT } from '../constants';
import { useOptimizedBackground, usePerformanceMonitoring, useFloatingParticles } from '../hooks/useAnimations';
import { StyledIcon } from '../components/animations';
import Footer from '../components/layout/Footer';

const CareersPage: React.FC = () => {
  usePerformanceMonitoring('CareersPage');
  
  const [jobs, setJobs] = useState<Job[]>([]);
  const [allJobs, setAllJobs] = useState<Job[]>([]); // Store all jobs for filter options
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeFilterDropdown, setActiveFilterDropdown] = useState<string | null>(null);
  const [filters, setFilters] = useState<JobFilters>({
    search: '',
    department: '',
    location: '',
    jobType: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Global floating particles for the careers page
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(80);
  
  // Background for hero section
  const { backgroundClasses, backgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  // Load jobs on component mount and filter change
  useEffect(() => {
    loadJobs();
  }, [filters]);

  // Load all jobs initially for filter options
  useEffect(() => {
    loadAllJobsForFilters();
  }, []);

  // Scroll to top effect
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadAllJobsForFilters = async () => {
    try {
      const response = await jobService.getJobs(); // Load all jobs without filters
      setAllJobs(response.jobs);
    } catch (error) {
      // Silent fail - filters will just show empty options
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await jobService.getJobs(filters);
      setJobs(response.jobs);
    } catch (error) {
      setJobs([]);
    }
    setLoading(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeFilterDropdown) {
        setActiveFilterDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeFilterDropdown]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is handled by filteredJobs useMemo
  };

  const handleFilterDropdown = (filterType: string) => {
    setActiveFilterDropdown(activeFilterDropdown === filterType ? null : filterType);
  };

  const handleFilterSelect = (filterType: string, value: string) => {
    setFilters({ ...filters, [filterType]: value });
    setActiveFilterDropdown(null);
  };

  // Custom FilterDropdown component
  const FilterDropdown: React.FC<{
    label: string;
    value: string | undefined;
    options: (string | undefined)[];
    filterType: string;
    placeholder: string;
  }> = ({ value, options, filterType, placeholder }) => {
    const isOpen = activeFilterDropdown === filterType;
    const validOptions = options.filter((option): option is string => Boolean(option));
    
    return (
      <div className="relative z-[100]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleFilterDropdown(filterType);
          }}
          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 transition-all duration-200 flex items-center justify-between hover:bg-gray-800/70"
        >
          <span className={value ? 'text-white' : 'text-gray-400'}>
            {value || placeholder}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {/* Dropdown Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 mt-2 w-full bg-gray-800 rounded-xl shadow-xl border border-gray-700 py-2 z-[999] max-h-60 overflow-y-auto"
            >
              <button
                onClick={() => handleFilterSelect(filterType, '')}
                className="block w-full text-left px-4 py-2 text-gray-400 hover:bg-gray-700 hover:text-primary-400 transition-colors duration-200"
              >
                {placeholder}
              </button>
              {validOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => handleFilterSelect(filterType, option)}
                  className={`block w-full text-left px-4 py-2 transition-colors duration-200 ${
                    value === option 
                      ? 'bg-primary-500/20 text-primary-400' 
                      : 'text-gray-300 hover:bg-gray-700 hover:text-primary-400'
                  }`}
                >
                  {option}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const benefits = [
    {
      icon: Sparkles,
      title: 'Innovation First',
      description: 'Work on cutting-edge AI and technology projects that shape the future',
      color: 'purple' as const,
    },
    {
      icon: Rocket,
      title: 'Career Growth',
      description: 'Accelerate your career with mentorship and continuous learning opportunities',
      color: 'blue' as const,
    },
    {
      icon: Users,
      title: 'Global Team',
      description: 'Collaborate with talented professionals from around the world',
      color: 'emerald' as const,
    },
    {
      icon: TrendingUp,
      title: 'Competitive Package',
      description: 'Enjoy competitive salaries, equity options, and comprehensive benefits',
      color: 'orange' as const,
    },
    {
      icon: Award,
      title: 'Work-Life Balance',
      description: 'Flexible hours and remote options to maintain a healthy lifestyle',
      color: 'cyan' as const,
    },
    {
      icon: Target,
      title: 'Impact',
      description: 'Make a real difference in transforming how businesses operate',
      color: 'green' as const,
    },
  ];

  // Get unique values for filters from all jobs (not filtered results)
  const departments = [...new Set(allJobs.map(job => job.department).filter(Boolean))];
  const locations = [...new Set(allJobs.map(job => job.location).filter(Boolean))];
  const jobTypes = [...new Set(allJobs.map(job => job.jobType).filter(Boolean))];

  return (
    <motion.div
      initial="hidden"
      animate="visible" 
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>
      
      {/* Page Content */}
      <div className="relative z-40">
        {/* Hero Section */}
        <section className="section-padding relative text-white">
          {/* Hero Background */}
          <div 
            className={backgroundClasses}
            style={backgroundStyle}
          />
          
          <div className="container relative z-40 pt-24">
            {/* Header Content - Constrained width */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                variants={ANIMATION_VARIANTS.slideUp}
                className="flex items-center justify-center mb-6"
              >
                <StyledIcon 
                  icon={Briefcase} 
                  size="xl" 
                  color="primary" 
                  variant="static"
                />
              </motion.div>

              <motion.h3 variants={ANIMATION_VARIANTS.slideUp}>
                <span className="text-sm md:text-base mb-8 text-gray-300 font-medium block tracking-[0.2em] uppercase relative">
                  <span 
                    className="bg-gradient-to-r from-gray-400 via-primary-300 to-gray-400 bg-clip-text text-transparent opacity-80"
                    style={{
                      animation: 'futuristicGlow 4s ease-in-out infinite'
                    }}
                  >
                    Join Our Mission
                  </span>
                  {/* Subtle futuristic underline */}
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-40"></span>
                  {/* Small side dots */}
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-1 h-1 bg-primary-400 rounded-full opacity-60"></span>
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-1 h-1 bg-primary-400 rounded-full opacity-60"></span>
                </span>
              </motion.h3>

              {/* Main Title */}
              <motion.h1
                variants={ANIMATION_VARIANTS.slideUp}
                className="heading-1 mb-6"
              >
                Careers at{' '}
                <span className="text-primary-gradient">
                  MTAI
                </span>
              </motion.h1>

              {/* Description */}
              <motion.p
                variants={ANIMATION_VARIANTS.slideUp}
                className="body-large max-w-3xl mx-auto"
              >
                Join us in building the future of AI-powered technology. 
                We're looking for passionate individuals who want to make a real impact.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Why Join Us Section */}
        <section className="relative py-48">
          {/* Background with rotation for alternating effect */}
          <div 
            className="absolute inset-0 bg-gradient-to-br from-gray-900/40 via-gray-800/20 to-gray-900/40"
            style={{
              transform: 'rotate(180deg)',
              transformOrigin: 'center center',
              backgroundPosition: 'bottom'
            }}
          />
          
          <div className="container relative z-40">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="heading-2 mb-12 text-center"
            >
              Why Join <span className="text-primary-gradient">MTAI?</span>
            </motion.h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <BenefitCard key={index} {...benefit} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Job Listings Section */}
        <section className="section-padding relative text-white">
          <div className="container relative z-40">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="heading-2 mb-4">
                Open <span className="text-primary-gradient">Positions</span>
              </h2>
              <p className="body-large">
                Find your next opportunity and join our growing team
              </p>
            </motion.div>

            {/* Search and Filters */}
            <div className="mb-8">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search positions..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 transition-all duration-200"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-6 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white hover:bg-gray-800 hover:border-primary-400/20 transition-all duration-200 flex items-center gap-2"
                >
                  <Filter className="w-5 h-5 flex-shrink-0" />
                  Filters
                </button>
                <button
                  type="submit"
                  className="btn-futuristic"
                >
                  Search
                </button>
              </form>

              {/* Filter Options */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid md:grid-cols-3 gap-4 p-6 bg-gray-900/40 backdrop-blur-xl border border-gray-800/50 rounded-xl mb-4 relative z-50"
                  >
                    <FilterDropdown
                      label="Department"
                      value={filters.department}
                      options={departments}
                      filterType="department"
                      placeholder="All Departments"
                    />
                    <FilterDropdown
                      label="Location"
                      value={filters.location}
                      options={locations}
                      filterType="location"
                      placeholder="All Locations"
                    />
                    <FilterDropdown
                      label="Job Type"
                      value={filters.jobType}
                      options={jobTypes}
                      filterType="jobType"
                      placeholder="All Types"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Job Cards Grid */}
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
              </div>
            ) : jobs.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {jobs.map((job, index) => (
                  <JobCard key={job.id} job={job} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg mb-4">No positions found matching your criteria.</p>
                <p className="text-gray-500">Try adjusting your filters or check back later for new opportunities.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-8">
          {/* Background with opposite rotation for alternating effect */}
          <div 
            className="absolute inset-0 bg-gradient-to-br from-gray-900/40 via-gray-800/20 to-gray-900/40"
            style={{
              transform: 'rotate(180deg)',
              transformOrigin: 'center center',
              backgroundPosition: 'bottom'
            }}
          />
          
          <div className="container relative z-40 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="heading-2 mb-6"
            >
              Don't See the <span className="text-primary-gradient">Right Role</span>?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="body-large mb-8 max-w-3xl mx-auto"
            >
              We're always looking for exceptional talent. Send us your resume and we'll keep you in mind for future opportunities.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <a
                href="mailto:careers@mtai.com"
                className="btn-futuristic"
              >
                Contact Our Team
              </a>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-50 p-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-200 hover:scale-110"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CareersPage;